import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    createStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchByIdStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed',
    editStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    deleteStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    pagination: {},
    error: null
};

export const fetchAllIncomeProducts = createAsyncThunk('product/fetchAllIncomeProducts', async ({
                                                                                                    scope,
                                                                                                    scopeId,
                                                                                                    roleSign,
                                                                                                    incomeTypeId,
                                                                                                    page,
                                                                                                    limit,
                                                                                                    orderPage = false
                                                                                                } = {}) => {
    try {
        let response;
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);

        const queryString = params.toString();
        const fullParams = queryString ? `&${queryString}` : '';
        if (scope.toLowerCase() === "restaurant") {
            response = await api.get(`/${roleSign}/restaurentProductTemplate?ttid=${incomeTypeId}${fullParams}`);
        } else if (orderPage) {
            response = await api.get(`/${roleSign}/order/product?id=${scopeId}`);
        } else {
            response = await api.get(`/${roleSign}/product?id=${scopeId}${fullParams}`);
        }
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const createIncomeProduct = createAsyncThunk('product/createIncomeProduct', async ({data}) => {
    try {
        const response = await api.post(`adg/restaurentProductTemplate/addNewProduct`, data, {
            useMultipart: true,
        });
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchIncomeProductById = createAsyncThunk('product/fetchIncomeProductById', async ({
                                                                                                    scope,
                                                                                                    roleSign,
                                                                                                    id
                                                                                                }) => {
    try {
        let response;
        if (scope.toLowerCase() === "restaurant") {
            response = await api.get(`/${roleSign}/restaurentProductTemplate/${id}`);
        } else {
            response = await api.get(`/${roleSign}/product/${id}`);
        }
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const editIncomeProduct = createAsyncThunk('product/editIncomeProduct', async ({
                                                                                              roleSign,
                                                                                              scope,
                                                                                              data,
                                                                                              id
                                                                                          }) => {
        try {
            const endpoint =
                scope.toLowerCase() === 'restaurant'
                    ? `/${roleSign}/restaurentProductTemplate/${id}`
                    : `/${roleSign}/product/${id}`;

            const response = await api.patch(endpoint, data, {
                useMultipart: true,
            });

            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

export const deleteIncomeProduct = createAsyncThunk('product/deleteIncomeProduct', async (id) => {
    try {
        const response = await api.delete(`/adg/product/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

const incomeProductSlice = createSlice({
    name: "product",
    initialState,
    reducers: {
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        resetEditStatus(state) {
            state.editStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder

            // Fetch Income Products
            .addCase(fetchAllIncomeProducts.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchAllIncomeProducts.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchAllIncomeProducts.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            //Create Income Product
            .addCase(createIncomeProduct.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createIncomeProduct.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createIncomeProduct.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch Income Product by ID
            .addCase(fetchIncomeProductById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchIncomeProductById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchIncomeProductById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            // Edit Income Product
            .addCase(editIncomeProduct.pending, (state) => {
                state.editStatus = 'loading';
            })
            .addCase(editIncomeProduct.fulfilled, (state, action) => {
                const {editStatus, error} = handleResponseStatus(action.payload, 'editStatus');
                state.editStatus = editStatus;
                state.error = error;
            })
            .addCase(editIncomeProduct.rejected, (state, action) => {
                state.editStatus = 'failed';
                state.error = action.error.message;
            })

            // Delete Income Product
            .addCase(deleteIncomeProduct.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteIncomeProduct.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                state.deleteStatus = deleteStatus;
                state.items = state.items.filter((item) => item.id !== action.payload.data.data._id);
                state.error = error;
            })
            .addCase(deleteIncomeProduct.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            })
    }
});

export const {
    resetCreateStatus,
    resetDeleteStatus,
    resetEditStatus,
} = incomeProductSlice.actions;
export default incomeProductSlice.reducer;
